import { useState, useEffect, useRef, useCallback } from 'react';
import { getAuth, signInWithPopup, GoogleAuthProvider, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { firebase } from '../../../../services/firebase';
import { checkAccess } from '../../../../utils/accessControl';
import { useResponsiveLayout } from '../../../../utils/hooks/useResponsiveLayout';
import { logError } from '../../../../utils/analytics';
import { DEMO_USER_EMAIL } from '../constant';
import moment from 'moment';

export const useLandingPage = () => {
  const navigate = useNavigate();
  const { isMobile } = useResponsiveLayout();
  const [isLoading, setIsLoading] = useState(false);
  const [accessError, setAccessError] = useState('');
  const [demoChatUrl, setDemoChatUrl] = useState('');

  const toast = useRef(null);
  const authListenerRef = useRef(null);
  const isInitializedRef = useRef(false);

  // Query Firebase for the demo user ID
  // Utility: Show persistent toast from localStorage if present
  const showPersistentAccessDeniedToast = (toastRef) => {
    const message = localStorage.getItem('accessDeniedToast');
    if (message && toastRef && toastRef.current) {
      toastRef.current.show({
        severity: 'error',
        summary: 'Access Denied',
        detail: message,
        life: 5000
      });
      localStorage.removeItem('accessDeniedToast');
    }
  };

  useEffect(() => {
    showPersistentAccessDeniedToast(toast);

    const fetchDemoUserId = async () => {
      try {
        // Query businessAtAGlance collection for the specific contact email
        const businessRef = collection(firebase, "businessAtAGlance");
        // Use constant for demo user contact email
        const q = query(businessRef, where("contactEmail", "==", DEMO_USER_EMAIL));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          // Get the first matching user
          const demoUser = querySnapshot.docs[0];
          const userId = demoUser.id;

          // Get the business name for SEO purposes
          const businessName = demoUser.data().shopName || "YesBoss-Demo";
          // Just replace spaces with hyphens to make it URL-friendly but keep it readable for SEO
          const urlBusinessName = businessName.replace(/\s+/g, '-');

          // Update the demoChatUrl with the found userId and business name for better SEO visibility
          setDemoChatUrl(`${window.location.origin}/${urlBusinessName}`);
        }
      } catch (error) {
        logError('useLandingPage: fetch demo user', error);
      }
    };

    fetchDemoUserId();
  }, []);

  // Auth state listener - Removed navigation logic to prevent conflicts
  // Navigation is now handled by ProtectedRoute and NavigationBar components
  useEffect(() => {
    if (isInitializedRef.current) return;
    
    isInitializedRef.current = true;
    const auth = getAuth();
    
    if (!authListenerRef.current) {
      authListenerRef.current = onAuthStateChanged(auth, () => {
        // Auth state changed, but we let other components handle navigation
      });
    }
    
    return () => {
      if (authListenerRef.current) {
        authListenerRef.current();
        authListenerRef.current = null;
      }
      isInitializedRef.current = false;
    };
  }, []);

  const handleAlternativeSignIn = useCallback(async (access_token, popupWindow) => {
    const auth = getAuth();
    try {
      const credential = GoogleAuthProvider.credential(null, access_token);
      const result = await auth.signInWithCredential(credential);
      popupWindow.close();

      const userRef = doc(firebase, "users", result.user.uid);
      const userSnap = await getDoc(userRef);
      if (!userSnap.exists()) {
        // User does not exist, check access control
        const { allowed, reason } = await checkAccess();
        if (!allowed) {
          localStorage.setItem('accessDeniedToast', reason || 'App exceed BETA testers');
          await signOut(auth);
          // No need to show toast here, will be handled on mount
          return;
        }
      }
      // Navigation is handled by ProtectedRoute and RootRedirect components
    } catch (error) {
      throw error;
    }
  }, [navigate]);

  const handleGoogleSignIn = useCallback(async () => {
    try {
      setIsLoading(true);
      setAccessError('');

      const auth = getAuth();
      const provider = new GoogleAuthProvider();

      provider.setCustomParameters({
        prompt: 'select_account'
      });

      try {
        if (window.location.hostname === 'localhost') {
          auth.settings.appVerificationDisabledForTesting = true;
        }
        const result = await signInWithPopup(auth, provider);

        const userRef = doc(firebase, "users", result.user.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          // User does not exist, check access control
          const { allowed, reason } = await checkAccess();
          if (!allowed) {
            localStorage.setItem('accessDeniedToast', reason || 'App exceed BETA testers');
            await signOut(auth);
            // No need to show toast here, will be handled on mount
            return;
          }
        }

        if (userSnap.exists()) {
          await updateDoc(userRef, {
            isActive: true,
            lastLogin: moment().unix(),
            lastActivity: moment().unix(),
          });
        }

        // Navigation is now handled by ProtectedRoute and RootRedirect components
      } catch (popupError) {
        if (popupError.code === 'auth/popup-blocked' || popupError.code === 'auth/popup-closed-by-user') {
          const width = 500;
          const height = 600;
          const left = window.screenX + (window.outerWidth - width) / 2;
          const top = window.screenY + (window.outerHeight - height) / 2;

          const popupWindow = window.open(
            `https://accounts.google.com/o/oauth2/v2/auth?client_id=${process.env.REACT_APP_GOOGLE_CLIENT_ID}&redirect_uri=${encodeURIComponent(window.location.origin)}&response_type=token&scope=email%20profile&prompt=select_account`,
            'Google Sign In',
            `width=${width},height=${height},left=${left},top=${top}`
          );

          const messageHandler = (event) => {
            if (event.origin === window.location.origin && event.data?.type === 'GOOGLE_SIGN_IN') {
              const { access_token } = event.data;
              if (access_token) {
                handleAlternativeSignIn(access_token, popupWindow)
                  .catch((error) => {
                    logError('useLandingPage: alternative sign-in', error);
                  });
                window.removeEventListener('message', messageHandler);
              }
            }
          };

          window.addEventListener('message', messageHandler);
        }
      }
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Sign-in Error',
        detail: 'An error occurred during sign-in. Please try again.',
        life: 5000
      });
    } finally {
      setIsLoading(false);
    }
  }, [navigate, handleAlternativeSignIn]);

  const handleTryDemo = useCallback(() => {
    window.open(demoChatUrl, '_blank');
  }, [demoChatUrl]);

  return {
    isMobile,
    isLoading,
    accessError,
    toast,
    demoChatUrl,
    handleGoogleSignIn,
    handleTryDemo
  };
}; 